import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import BugReportIcon from "@mui/icons-material/BugReport";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";

import apiService from "../../service/Api";
import { RootState } from "../../store/Index";
import {
  BugAssignment,
  BugAssignmentResponse,
} from "../../store/slice/bug-fix/types";
import { consumeBugAssignmentPrompt } from "../../utils/bugAssignmentPrompt";

const priorityColors = {
  low: "default",
  medium: "warning",
  high: "error",
  urgent: "error",
} as const;

const BugAssignmentModal = () => {
  const navigate = useNavigate();
  const userId = useSelector((state: RootState) => state.auth.user?.id);
  const [assignments, setAssignments] = useState<BugAssignment[]>([]);
  const [open, setOpen] = useState(false);
  const shouldCheckAfterLogin = useRef(false);

  useEffect(() => {
    if (!userId) {
      shouldCheckAfterLogin.current = false;
      return;
    }
    if (!shouldCheckAfterLogin.current) {
      shouldCheckAfterLogin.current = consumeBugAssignmentPrompt();
    }
    if (!shouldCheckAfterLogin.current) return;

    let active = true;

    apiService
      .get<BugAssignmentResponse>(
        `/api/bug-report/bug-assignments/?assigned_to=${userId}&page_size=100`,
      )
      .then(({ data }) => {
        if (!active) return;

        const outstanding = (data.results ?? []).filter(
          ({ bug }) => bug.status !== "resolved",
        );
        setAssignments(outstanding);
        setOpen(outstanding.length > 0);
        shouldCheckAfterLogin.current = false;
      })
      // Users without access to bug assignments should not see an error prompt.
      .catch(() => {
        shouldCheckAfterLogin.current = false;
      });

    return () => {
      active = false;
    };
  }, [userId]);

  const visibleAssignments = useMemo(() => assignments.slice(0, 3), [assignments]);

  const viewAssignments = () => {
    setOpen(false);
    navigate("/bug-reports");
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <BugReportIcon color="error" />
          <Box>
            <Typography variant="h6" fontWeight={700}>
              You have bug assignments
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {assignments.length} outstanding {assignments.length === 1 ? "bug needs" : "bugs need"} your attention.
            </Typography>
          </Box>
        </Stack>
        <IconButton
          aria-label="Close bug assignments"
          onClick={() => setOpen(false)}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={1.5}>
          {visibleAssignments.map((assignment) => (
            <Box
              key={assignment.id ?? assignment.bug.id}
              sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2 }}
            >
              <Stack direction="row" justifyContent="space-between" spacing={2}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary">
                    {assignment.bug.id} · {assignment.bug.module_name}
                  </Typography>
                  <Typography fontWeight={600} noWrap>
                    {assignment.bug.description}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={assignment.priority}
                  color={priorityColors[assignment.priority]}
                  sx={{ textTransform: "capitalize", flexShrink: 0 }}
                />
              </Stack>
            </Box>
          ))}

          {assignments.length > visibleAssignments.length && (
            <Typography variant="body2" color="text.secondary" textAlign="center">
              And {assignments.length - visibleAssignments.length} more
            </Typography>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={() => setOpen(false)} color="inherit">
          Not now
        </Button>
        <Button onClick={viewAssignments} variant="contained">
          View assignments
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BugAssignmentModal;
